import React from "react"
import {
  formatCurrency,
  formatPercent,
  formatRatio,
  formatAge,
} from "./tableFormatters"
import {
  ToolTable,
  ToolTableHeader,
  ToolTableBody,
  ToolTableRow,
  ToolTableHead,
  ToolTableCell,
} from "@/ui/ToolTable"

const toTitleCase = (str) => {
  return String(str || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const PerformanceTable = ({
  data,
  columnHeader,
  dataProperty,
}: {
  data: any[]
  columnHeader: string
  dataProperty: string
}) => {
  const transformedData = data.map((row) => ({
    groupingValue: toTitleCase(row[dataProperty]),
    committed: parseFloat(row.committed) || 0,
    called: parseFloat(row.called) || 0,
    distributed: parseFloat(row.distributed) || 0,
    marketValue: parseFloat(row.market_value) || 0,
    tvpi: parseFloat(row.tvpi) || 0,
    irr: parseFloat(row.irr) || 0,
    pAndL: parseFloat(row.p_and_l) || 0,
    age: parseFloat(row.age) || 0,
  }))

  const totals = transformedData.reduce(
    (acc, row) => ({
      committed: acc.committed + row.committed,
      called: acc.called + row.called,
      distributed: acc.distributed + row.distributed,
      marketValue: acc.marketValue + row.marketValue,
      pAndL: acc.pAndL + row.pAndL,
    }),
    {
      committed: 0,
      called: 0,
      distributed: 0,
      marketValue: 0,
      pAndL: 0,
    },
  )

  const totalCalled = totals.called
  const avgTVPI =
    totalCalled > 0
      ? (totals.distributed + totals.marketValue) / totalCalled
      : 0
  const avgIRR =
    transformedData.reduce((sum, row) => sum + row.irr, 0) /
    transformedData.length
  const avgAge =
    transformedData.reduce((sum, row) => sum + row.age, 0) /
    transformedData.length

  return (
    <ToolTable>
      <ToolTableHeader>
        <ToolTableRow>
          <ToolTableHead>{columnHeader}</ToolTableHead>
          <ToolTableHead>Committed</ToolTableHead>
          <ToolTableHead>Called</ToolTableHead>
          <ToolTableHead>Distributed</ToolTableHead>
          <ToolTableHead>Market Value</ToolTableHead>
          <ToolTableHead>TVPI</ToolTableHead>
          <ToolTableHead>IRR</ToolTableHead>
          <ToolTableHead>P&L</ToolTableHead>
          <ToolTableHead>Age</ToolTableHead>
        </ToolTableRow>
      </ToolTableHeader>
      <ToolTableBody>
        {transformedData.map((row, index) => (
          <ToolTableRow key={index}>
            <ToolTableCell>{row.groupingValue}</ToolTableCell>
            <ToolTableCell>{formatCurrency(row.committed)}</ToolTableCell>
            <ToolTableCell>{formatCurrency(row.called)}</ToolTableCell>
            <ToolTableCell>{formatCurrency(row.distributed)}</ToolTableCell>
            <ToolTableCell>{formatCurrency(row.marketValue)}</ToolTableCell>
            <ToolTableCell>{formatRatio(row.tvpi)}</ToolTableCell>
            <ToolTableCell>{formatPercent(row.irr)}</ToolTableCell>
            <ToolTableCell>{formatCurrency(row.pAndL)}</ToolTableCell>
            <ToolTableCell>{formatAge(row.age)}</ToolTableCell>
          </ToolTableRow>
        ))}
        <ToolTableRow variant="totals">
          <ToolTableCell>Total</ToolTableCell>
          <ToolTableCell>{formatCurrency(totals.committed)}</ToolTableCell>
          <ToolTableCell>{formatCurrency(totals.called)}</ToolTableCell>
          <ToolTableCell>{formatCurrency(totals.distributed)}</ToolTableCell>
          <ToolTableCell>{formatCurrency(totals.marketValue)}</ToolTableCell>
          <ToolTableCell>{formatRatio(avgTVPI)}</ToolTableCell>
          <ToolTableCell>{formatPercent(avgIRR)}</ToolTableCell>
          <ToolTableCell>{formatCurrency(totals.pAndL)}</ToolTableCell>
          <ToolTableCell>{formatAge(avgAge)}</ToolTableCell>
        </ToolTableRow>
      </ToolTableBody>
    </ToolTable>
  )
}

const EmptyTable = () => {
  return (
    <div className="editorjs-empty-tool-table">No table data available</div>
  )
}

const hasValidTableData = (data: any) => {
  return data && Array.isArray(data) && data.length > 0
}

// Configuration interface
interface PerformanceTableConfig {
  type: string
  headerText: string
  columnHeader: string
  dataProperty: string
  toolboxTitle: string
  toolboxIcon?: string
  className?: string
}

export const createPerformanceTableTool = (config: PerformanceTableConfig) => {
  const {
    type,
    headerText,
    columnHeader,
    dataProperty,
    toolboxTitle,
    toolboxIcon = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/><path d="m16 15-2-2 2-2"/><path d="M21 9H3"/><path d="M21 15H3"/></svg>',
    className,
  } = config

  return class PerformanceTableTool {
    api: any
    block: any
    createPortal: any
    removePortal: any
    portal: any
    tableData: any
    headerInserted: boolean

    constructor(toolConfig: any) {
      this.block = toolConfig.block
      this.api = toolConfig.api
      this.createPortal = toolConfig.config?.createPortal
      this.removePortal = toolConfig.config?.removePortal
      this.tableData = toolConfig.config?.tableData

      this.headerInserted = toolConfig.data?.header_inserted || false

      return this
    }

    static get toolbox() {
      return {
        title: toolboxTitle,
        icon: toolboxIcon,
      }
    }

    render() {
      const rootEl = document.createElement("div")
      if (className) {
        rootEl.className = className
      }

      const hasData = hasValidTableData(this.tableData)

      // Check if we need to insert a header above this block
      queueMicrotask(() => {
        this.checkAndInsertHeaderIfNeeded()
      })

      this.portal = this.createPortal(
        this.block.id,
        hasData ? (
          <PerformanceTable
            data={this.tableData}
            columnHeader={columnHeader}
            dataProperty={dataProperty}
          />
        ) : (
          <EmptyTable />
        ),
        rootEl,
      )

      return rootEl
    }

    async checkAndInsertHeaderIfNeeded() {
      try {
        // Short-circuit if header has already been inserted
        if (this.headerInserted) {
          return
        }

        // Find this block's index by searching through all blocks
        let currentBlockIndex = -1
        const totalBlocks = this.api.blocks.getBlocksCount()

        for (let i = 0; i < totalBlocks; i++) {
          const block = this.api.blocks.getBlockByIndex(i)
          if (block && block.id === this.block.id) {
            currentBlockIndex = i
            break
          }
        }

        if (currentBlockIndex === -1) {
          console.error(
            "ERROR: Could not find current block, skipping header insertion",
          )
          return
        }

        if (currentBlockIndex > 0) {
          const previousBlock = this.api.blocks.getBlockByIndex(
            currentBlockIndex - 1,
          )

          if (previousBlock) {
            // Await the saved data from the previous block
            const previousBlockData = await previousBlock.save()

            // Check if it's a header block with our specific text and level
            if (
              previousBlockData.tool === "header" &&
              previousBlockData.data.text === headerText &&
              previousBlockData.data.level === 3
            ) {
              // Header exists - mark as inserted even though we didn't insert it
              this.headerInserted = true
              return
            }
          }
        }

        this.insertHeaderBlock(currentBlockIndex)
      } catch (error) {
        console.error("Error in checkAndInsertHeaderIfNeeded:", error)
      }
    }

    insertHeaderBlock(currentBlockIndex: number) {
      try {
        // Insert a header block above the current table block
        this.api.blocks.insert(
          "header",
          {
            text: headerText,
            level: 3,
          },
          {},
          currentBlockIndex,
          true,
        )

        this.headerInserted = true

        // Move focus back to the table block after insertion
        setTimeout(() => {
          this.api.caret.setToBlock(currentBlockIndex + 1)
        }, 50)
      } catch (error) {
        console.error("Error inserting header block:", error)
      }
    }

    remove() {
      const totalBlocks = this.api.blocks.getBlocksCount()
      for (let i = 0; i < totalBlocks; i++) {
        const block = this.api.blocks.getBlockByIndex(i)
        if (block && block.id === this.block.id) {
          this.api.blocks.delete(i)
          break
        }
      }
    }

    removed() {
      this.removePortal(this.block.id)
    }

    save() {
      return {
        type: type,
        header_inserted: this.headerInserted,
      }
    }

    static get isReadOnlySupported() {
      return true
    }
  }
}
